<script>
import { mapGetters } from 'vuex';
import EventPositionPrizeMoney from './EventPositionPrizeMoney.vue';
export default {
  name: 'EventHeaderDropdown',
  components: {
    EventPositionPrizeMoney,
  },
  props: {
    dropdownType: {
      type: String,
      required: true,
      validator: function(value) {
        return ['eventCondition', 'prizeMoney', 'trackDistance', 'trackCondition'].indexOf(value) !== -1;
      },
    },
  },
  data() {
    return {
      isActive: false,
    };
  },
  computed: {
    ...mapGetters({
      eventData: 'FormGuideEventHeader/getEventData',
      venue: 'FormGuideEventHeader/getVenue',
      prizeMoney: 'FormGuideEventHeader/getPrizeMoney',
      entryClass: 'FormGuideEventHeader/getEntryClass',
      entryCondition: 'FormGuideEventHeader/getEntryCondition',
      totalPrizeMoney: 'FormGuideEventHeader/getTotalPrizeMoney',
      weather: 'FormGuideEventHeader/getWeather',
      raceType: 'FormGuideEventHeader/getRaceType',
    }),
    eventInfo() {
      return this.eventData || {};
    },
    isEventCondition() {
      return this.dropdownType == 'eventCondition';
    },
    isPrizeMoney() {
      return this.dropdownType == 'prizeMoney';
    },
    isTrackDistance() {
      return this.dropdownType == 'trackDistance';
    },
    isTrackCondition() {
      return this.dropdownType == 'trackCondition';
    },
    roundTemperature() {
      const { weather } = this;
      if (weather) {
        const { temperature, temperature_units } = weather;
        return temperature ? `${Math.round(temperature)}${temperature_units}` : 0;
      }
      return '';
    },
    roundFeelsLike() {
      const { weather } = this;
      if (weather) {
        const { feels_like, temperature_units } = weather;
        return feels_like ? `${Math.round(feels_like)}${temperature_units}` : 0;
      }
      return '';
    },
    getUnitDistance() {
      const { eventData } = this;
      if (eventData) {
        const unit = eventData.distance_unit == 'meter' ? 'm' : '';
        return `${eventData.distance}${unit}`;
      }
      return '';
    },
    hasSprintLane() {
      const { venue } = this;
      return venue.sprint_lane === 1 ? 'YES' : 'NO';
    },
    getWeatherIcon() {
      const { weather } = this;
      return weather ? `https://puntcdn.com/weather/grey/${weather.condition_icon}.svg` : '';
    },
    getWeatherCondition() {
      const { weather } = this;
      return weather ? weather.condition : '';
    },
    iconClassName() {
      const { dropdownType } = this;
      const classNameMap = {
        eventCondition: 'event-condition',
        prizeMoney: 'prize-money',
        trackDistance: 'track-dist',
        trackCondition: 'track-condition',
      };
      return classNameMap[dropdownType];
    },
    getMainText() {
      const {
        isEventCondition,
        isPrizeMoney,
        isTrackDistance,
        isTrackCondition,
        entryClass,
        totalPrizeMoney,
        getUnitDistance,
      } = this;

      if (isEventCondition) return `${entryClass}`;
      if (isPrizeMoney) return `$${totalPrizeMoney}`;
      if (isTrackDistance) return `${getUnitDistance}`;
      if (isTrackCondition) return `Good`;
    },
    placeWinners() {
      const { eventData } = this;
      return eventData.place_winners || 3;
    },
  },
  methods: {
    mouseOver() {
      this.isActive = true;
    },
    mouseLeave() {
      this.isActive = false;
    },
  },
};
</script>

<template>
  <div class="event-header-dropdown">
    <div class="event-header-dropdown__box" @mouseover="mouseOver">
      <div class="event-header-dropdown__box-container">
        <div class="event-header-dropdown__box-left">
          <div :class="`${iconClassName} event-header-dropdown__box-icon`"></div>
          <div class="event-header-dropdown__box-text">{{ getMainText }}</div>
        </div>
        <div class="event-header-dropdown__box-right">
          <div class="event-header-dropdown__box-more"></div>
        </div>
      </div>
    </div>
    <div v-show="isActive" class="event-header-dropdown__hover-box" @mouseleave="mouseLeave">
      <div class="event-header-dropdown__hover-box-container">
        <div class="event-header-dropdown__hover-box-left">
          <div :class="`${iconClassName} event-header-dropdown__hover-box-icon`"></div>
          <div class="event-header-dropdown__hover-box-text">{{ getMainText }}</div>
          <div class="event-header-dropdown__details">
            <!-- event condition details -->
            <div
              v-show="isEventCondition"
              class="event-header-dropdown__details-event-condition"
            >{{ entryCondition }}</div>

            <!-- prize money details -->
            <div v-show="isPrizeMoney" class="event-header-dropdown__details-prize">
              <template v-for="(prize, index) in prizeMoney">
                <EventPositionPrizeMoney
                  :position-no="prize.position"
                  :prize-money="prize.value"
                  :place-winners="placeWinners"
                  :key="`prize-money-${index}`"
                />
              </template>
            </div>

            <!-- track distance details -->
            <div v-show="isTrackDistance" class="event-header-dropdown__details-track-dist">
              <div class="event-header-dropdown__info-container">
                <div class="event-header-dropdown__info-header">CIRC</div>
                <div class="event-header-dropdown__info-details">{{ venue.circumference }}m</div>
              </div>
              <div class="event-header-dropdown__info-container">
                <div class="event-header-dropdown__info-header">STRAIGHT</div>
                <div class="event-header-dropdown__info-details">{{ venue.home_straight_length }}m</div>
              </div>
              <template v-if="raceType === 'harness'">
                <div class="event-header-dropdown__info-container">
                  <div class="event-header-dropdown__info-header">FRONT LINE</div>
                  <div
                    class="event-header-dropdown__info-details"
                  >{{ eventInfo.number_across_front }}</div>
                </div>
                <div class="event-header-dropdown__info-container">
                  <div class="event-header-dropdown__info-header">START TYPE</div>
                  <div class="event-header-dropdown__info-details">{{ eventInfo.start_type }}</div>
                </div>
                <div class="event-header-dropdown__info-container">
                  <div class="event-header-dropdown__info-header">SPRINT LANE</div>
                  <div class="event-header-dropdown__info-details">{{ hasSprintLane }}</div>
                </div>
              </template>
              <template v-else>
                <div class="event-header-dropdown__info-container">
                  <div class="event-header-dropdown__info-header">RAIL</div>
                  <div
                    class="event-header-dropdown__info-details"
                  >Out 3m From 1400 - 300, Cutaway Rail</div>
                </div>
              </template>
            </div>

            <!-- track conditions details -->
            <div v-show="isTrackCondition" class="event-header-dropdown__details-track-condition">
              <img :src="getWeatherIcon" class="event-header-dropdown__weather-icon" alt >
              <div class="event-header-dropdown__degree-desc">
                <div class="event-header-dropdown__weather-degrees">{{ roundTemperature }}</div>
                <div
                  class="event-header-dropdown__weather-feels-like"
                >(feels like {{ roundFeelsLike }})</div>
              </div>
              <div class="event-header-dropdown__weather-desc">{{ getWeatherCondition }}</div>
            </div>
          </div>
        </div>
        <div class="event-header-dropdown__hover-box-right">
          <div class="event-header-dropdown__hover-box-more"></div>
        </div>
      </div>
    </div>
  </div>
</template>
